
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Profile, fetchProfile, updateProfile } from "@/services/profiles";

export const fetchUserProfile = async (userId: string): Promise<Profile | null> => {
  try {
    console.log("Fetching user profile for user ID:", userId);
    const profileData = await fetchProfile(userId);
    console.log("Profile data retrieved:", profileData);
    
    if (profileData) {
      console.log("Profile state updated with:", profileData);
      return profileData;
    } else {
      console.warn("No profile data returned for user:", userId);
      return null;
    }
  } catch (error) {
    console.error("Error in fetchUserProfile:", error);
    toast.error("Failed to load user profile");
    // Return null instead of throwing, so the app can continue
    return null;
  }
};

export const updateUserProfile = async (userId: string, updates: Partial<Profile>): Promise<Profile | null> => {
  if (!userId) {
    toast.error("You must be logged in to update your profile");
    return null;
  }
  
  const updatedProfile = await updateProfile(userId, updates);
  return updatedProfile;
};

export const signUp = async (
  email: string, 
  password: string, 
  profileData?: {
    display_username: string;
    university_id: string;
    lab_id: string;
  }
): Promise<void> => {
  try {
    console.log("Attempting to register user:", email);
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: profileData || {}
      }
    });

    if (error) {
      console.error("Registration error:", error);
      toast.error(error.message);
      throw error;
    }

    if (data.user) {
      console.log("User registered successfully:", data.user.id);
      toast.success("Registration successful! Please check your email for confirmation and await approval.");
    }
  } catch (error) {
    console.error("Sign up error:", error);
    if (error instanceof Error) {
      throw new Error(error.message);
    }
    throw error;
  }
};

export const signIn = async (email: string, password: string) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      console.error("Sign in error details:", { error });
      toast.error(error.message);
      throw new Error(error.message);
    }

    if (data.user) {
      toast.success("Login successful!");
    }
    
    return data;
  } catch (error) {
    console.error("Sign in error:", error);
    if (error instanceof Error) {
      throw new Error(error.message);
    }
    throw error;
  }
};

export const signOut = async (): Promise<void> => {
  try {
    console.log("Attempting to sign out...");
    
    // Clear all local storage related to Supabase
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('sb-') || key.includes('supabase'))) {
        keysToRemove.push(key);
      }
    }
    
    // Remove the keys in a separate loop to avoid issues with changing localStorage during iteration
    keysToRemove.forEach(key => {
      console.log(`Removing local storage key: ${key}`);
      localStorage.removeItem(key);
    });
    
    // Then call the signOut method
    const { error } = await supabase.auth.signOut({
      scope: 'local' // Explicitly specify local scope to ensure we're only signing out locally
    });
    
    if (error) {
      console.error("Sign out error:", error);
      toast.error(error.message);
      throw error;
    }
    
    console.log("Sign out successful");
    toast.success("You have been logged out");
    
    // Delay the page reload to allow the toast to be seen
    setTimeout(() => {
      console.log("Forcing page reload after sign out");
      window.location.href = '/';
    }, 1000);
  } catch (error) {
    console.error("Sign out error:", error);
    toast.error("Failed to sign out: " + (error instanceof Error ? error.message : "Unknown error"));
    
    // Even if there's an error, try to force a reload as a fallback
    setTimeout(() => {
      console.log("Forcing page reload after sign out error");
      window.location.reload();
    }, 1000);
  }
};
